import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';

export type BuyerType = 'Individual' | 'Corporate';
export type BuyerLocation = 'domestic' | 'eu' | 'non_eu';

export type TInvoicingCompany = {
  id: number;
  name: string;
  registrationNumber: string | null;
  vatNumber: string | null;
  legalAddress: string;
  countryCode: string;
  vatRate: number;
  /**
   * Whether the jurisdiction levies VAT at all. Deployed from the VAT manifest
   * and not editable here: a company reclassified by hand would decide VAT one
   * way and be selected another.
   */
  vatRegime: 'standard' | 'none';
  /** How a no-VAT entity prints the tax line. Null where VAT applies. */
  vatLineDisplay: 'omit' | 'dash' | null;
  priority: number;
  isActive: boolean;
  website: string | null;
  /**
   * The status label this company is written as on Monday. The Finance board and
   * the Accounting Requests subitems board spell the same entity differently, so
   * they are separate fields. Null leaves the Monday cell empty.
   */
  mondayFinanceLabel: string | null;
  mondayArLabel: string | null;
  createdAt: string;
  updatedAt: string;
  vatRateCount: number;
  bankAccountCount: number;
  activeBankAccountCount: number;
  cryptoWalletCount: number;
  activeCryptoWalletCount: number;
  cryptoDisallowed: boolean;
  invoiceCount: number;
};

export type TReadinessReport = {
  ready: boolean;
  issues: string[];
};

/**
 * When one field or one row was last written, and by whom. Absent where nothing
 * was ever recorded for it - there is no backfill, so a blank means unknown,
 * never unchanged.
 */
export type TChangeStamp = {
  id: number;
  at: string;
  operation: 'created' | 'updated';
  actor: string | null;
};

export type TVatRate = {
  id: number;
  lastChange: TChangeStamp | null;
  companyId: number;
  buyerType: BuyerType;
  buyerLocation: BuyerLocation;
  buyerVatRegistered: boolean | null;
  vatRate: number;
  invoiceNote: string | null;
};

export type TBankAccount = {
  id: number;
  lastChange: TChangeStamp | null;
  companyId: number;
  currency: string;
  beneficiaryName: string;
  beneficiaryAddress: string | null;
  accountNumber: string;
  bankName: string;
  bankAddress: string | null;
  swiftCode: string | null;
  correspondentBankName: string | null;
  correspondentBankCountry: string | null;
  correspondentBankSwift: string | null;
  isActive: boolean;
};

export type TCryptoWallet = {
  id: number;
  lastChange: TChangeStamp | null;
  companyId: number;
  chain: string;
  network: string;
  walletAddress: string;
  acceptedTokens: string[];
  isActive: boolean;
};

export type TInvoicingRule = {
  id: number;
  ruleType:
    | 'jurisdiction'
    | 'industry'
    | 'paying_party_country'
    | 'payment_method'
    | 'service_category';
  ruleKey: string;
  isAllowed: boolean;
};

/**
 * Newest recorded change to the company's details. Activation and deactivation
 * are recorded too but never reported here: they are operational status, not
 * how old the details an accountant reads are.
 */
export type TLastRecordedChange = {
  id: number;
  at: string;
  entity: 'company' | 'bank_account' | 'crypto_wallet' | 'vat_rate';
  entityLabel: string;
  operation: 'created' | 'updated' | 'removed';
  label: string | null;
  actor: string | null;
};

export type TInvoicingCompanyDetail = TInvoicingCompany & {
  vatRates: TVatRate[];
  bankAccounts: TBankAccount[];
  cryptoWallets: TCryptoWallet[];
  /** Maintainable exclusions layered on top of the built-in selection rules. */
  rules: TInvoicingRule[];
  /** Null until a change is recorded; there is no backfill for older edits. */
  lastRecordedChange: TLastRecordedChange | null;
  /** Per-header-field stamps, keyed by the field name. Absent where unrecorded. */
  fieldChanges: Record<string, TChangeStamp | undefined>;
};

export const keys = {
  all: ['invoicingCompanies'] as const,
  list: () => [...keys.all, 'list'] as const,
  detail: (id: number) => [...keys.all, 'detail', id] as const,
};

const post = <T>(path: string, body: unknown) =>
  call<T>(`invoicing/${path}`, { json: body as any }).then((r) => r.json());

export const useInvoicingCompanies = () =>
  useQuery<TInvoicingCompany[]>({
    queryKey: keys.list(),
    queryFn: () =>
      call<TInvoicingCompany[]>('invoicing/companies', {
        method: 'GET',
      }).then((r) => r.json()),
  });

export const useInvoicingCompany = (id: number) =>
  useQuery<TInvoicingCompanyDetail>({
    queryKey: keys.detail(id),
    queryFn: () => post<TInvoicingCompanyDetail>('companies/detail', { id }),
    enabled: id > 0,
  });

const invalidateDetail = (id: number) => {
  queryClient.invalidateQueries({ queryKey: keys.detail(id) });
  queryClient.invalidateQueries({ queryKey: keys.list() });
};
const invalidateList = () =>
  queryClient.invalidateQueries({ queryKey: keys.list() });

export const useCreateCompany = () =>
  useMutation({
    mutationFn: (input: Partial<TInvoicingCompany>) =>
      post<TInvoicingCompany>('companies/create', input),
    onSuccess: invalidateList,
  });

export const useUpdateCompany = () =>
  useMutation({
    mutationFn: ({
      id,
      ...rest
    }: { id: number } & Partial<TInvoicingCompany>) =>
      post<TInvoicingCompany>('companies/update', { id, ...rest }),
    onSuccess: (_, { id }) => invalidateDetail(id),
  });

export const useDeleteCompany = () =>
  useMutation({
    mutationFn: (id: number) => post<void>('companies/delete', { id }),
    onSuccess: invalidateList,
  });

export const useActivateCompany = () =>
  useMutation({
    mutationFn: (id: number) =>
      post<TInvoicingCompany>('companies/activate', { id }),
    onSuccess: (_, id) => invalidateDetail(id),
  });

export const useCompanyReadiness = (id: number) =>
  useQuery<TReadinessReport>({
    queryKey: [...keys.detail(id), 'readiness'],
    queryFn: () => post<TReadinessReport>('companies/readiness', { id }),
    enabled: id > 0,
  });

export const useDeactivateCompany = () =>
  useMutation({
    mutationFn: (id: number) =>
      post<TInvoicingCompany>('companies/deactivate', { id }),
    onSuccess: (_, id) => invalidateDetail(id),
  });

export const useUpsertVatRate = (companyId: number) =>
  useMutation({
    mutationFn: (input: Omit<TVatRate, 'id' | 'companyId' | 'lastChange'>) =>
      post<TVatRate>('vat-rates/upsert', { companyId, ...input }),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useDeleteVatRate = (companyId: number) =>
  useMutation({
    mutationFn: (id: number) => post<void>('vat-rates/delete', { id }),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useCloneVatRatesFromActive = (companyId: number) =>
  useMutation({
    mutationFn: () =>
      post<{ cloned: number; rates: TVatRate[] }>(
        'vat-rates/clone-from-active',
        { companyId },
      ),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useCreateBankAccount = (companyId: number) =>
  useMutation({
    mutationFn: (
      input: Omit<TBankAccount, 'id' | 'companyId' | 'lastChange'>,
    ) => post<TBankAccount>('bank-accounts/create', { companyId, ...input }),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useUpdateBankAccount = (companyId: number) =>
  useMutation({
    mutationFn: (input: Omit<TBankAccount, 'companyId' | 'lastChange'>) =>
      post<TBankAccount>('bank-accounts/update', input),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useDeleteBankAccount = (companyId: number) =>
  useMutation({
    mutationFn: (id: number) => post<void>('bank-accounts/delete', { id }),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useCreateCryptoWallet = (companyId: number) =>
  useMutation({
    mutationFn: (
      input: Omit<TCryptoWallet, 'id' | 'companyId' | 'lastChange'>,
    ) => post<TCryptoWallet>('crypto-wallets/create', { companyId, ...input }),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useUpdateCryptoWallet = (companyId: number) =>
  useMutation({
    mutationFn: (input: Omit<TCryptoWallet, 'companyId' | 'lastChange'>) =>
      post<TCryptoWallet>('crypto-wallets/update', input),
    onSuccess: () => invalidateDetail(companyId),
  });

export const useDeleteCryptoWallet = (companyId: number) =>
  useMutation({
    mutationFn: (id: number) => post<void>('crypto-wallets/delete', { id }),
    onSuccess: () => invalidateDetail(companyId),
  });
